import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Shared/contexts/AuthContext';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import AddStudentModal from '../components/AddStudentModal';
import BillingWarningBanner from '../../Billing/components/BillingWarningBanner';
import { useTier } from '../../../hooks/useTier';
import type { StudentProfile } from '../../Shared/types';

const ParentHome: React.FC = () => {
  const { user } = useAuth();
  const { billingActions } = useTier();
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);

  const loadStudents = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.getStudents();
      setStudents(response.students);
    } catch (err: unknown) {
      setError(extractApiError(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStudents();
  }, [loadStudents]);

  return (
    <Navbar>
      <BillingWarningBanner show={billingActions?.show_payment_warning ?? false} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome, {user?.name?.split(' ')[0] || 'Parent'}! 👋
            </h1>
            <p className="text-lg text-gray-600">Manage your students and track their progress</p>
          </div>
          <Link
            to="/billing"
            className="text-sm text-gray-500 hover:text-gray-700 underline shrink-0 mt-2"
          >
            Manage Billing
          </Link>
        </div>

        {/* Add Student Button */}
        <div className="mb-6 flex items-center gap-4">
          <Button onClick={() => setIsAddStudentModalOpen(true)} className="text-lg">
            + Add Student
          </Button>
          {billingActions?.can_upgrade && (
            <Link
              to="/billing/pricing"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
            >
              Upgrade Plan
            </Link>
          )}
        </div>

        {/* Students List */}
        {isLoading ? (
          <Card>
            <LoadingSpinner />
          </Card>
        ) : error ? (
          <Card>
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={() => void loadStudents()} variant="secondary">
                Try Again
              </Button>
            </div>
          </Card>
        ) : students.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-lg text-gray-600 mb-4">You haven't added any students yet.</p>
              <p className="text-gray-500 mb-6">
                Add your first student to get started with their learning journey!
              </p>
              <Button onClick={() => setIsAddStudentModalOpen(true)}>Add Your First Student</Button>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {students.map((student) => (
              <Card key={student.id} className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {student.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900">{student.name}</h3>
                    <p className="text-sm text-gray-500">Grade {student.grade_level}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">PIN</span>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {student.pin}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Status</span>
                    <span
                      className={`text-sm px-2 py-1 rounded ${
                        student.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {student.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Assessment</span>
                    {student.has_completed_assessment ? (
                      <span className="text-sm text-green-600">✓ Complete</span>
                    ) : (
                      <span className="text-sm text-yellow-600">Pending</span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add Student Modal */}
        <AddStudentModal
          isOpen={isAddStudentModalOpen}
          onClose={() => setIsAddStudentModalOpen(false)}
          onStudentAdded={() => {
            setIsAddStudentModalOpen(false);
            void loadStudents();
          }}
        />
      </div>
    </Navbar>
  );
};

export default ParentHome;
